import { useState, useEffect, useRef } from "react";
import { axiosInstance } from "../App";
import { useBox } from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, User, Phone, Mail, Edit, Trash2, Upload, Download, FileSpreadsheet, Clock } from "lucide-react";
import { getErrorMessage } from "../utils/errorHandler";
import CustomerHistoryDialog from "../components/CustomerHistoryDialog";

export default function Customers({ user }) {
  const { selectedBox } = useBox();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [password, setPassword] = useState("");
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: "",
    cedula: "",
    phone: "",
    email: ""
  });

  const fetchCustomers = async () => {
    try {
      const url = selectedBox ? `/customers?box_id=${selectedBox.id}` : '/customers';
      const response = await axiosInstance.get(url);
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBox) {
      fetchCustomers();
    }
  }, [selectedBox]);

  const handleCreateCustomer = async () => {
    if (!selectedBox) {
      toast.error("Selecciona una caja primero");
      return;
    }
    
    try {
      const dataWithBox = { ...formData, box_id: selectedBox.id };
      await axiosInstance.post('/customers', dataWithBox);
      toast.success("Cliente creado exitosamente");
      setShowDialog(false);
      setFormData({ name: "", cedula: "", phone: "", email: "" });
      fetchCustomers();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al crear cliente"));
    }
  };

  const handleEditCustomer = async () => {
    try {
      await axiosInstance.put(`/customers/${selectedCustomer.id}?password=${password}`, formData);
      toast.success("Cliente actualizado exitosamente");
      setShowEditDialog(false);
      setSelectedCustomer(null);
      setPassword("");
      setFormData({ name: "", cedula: "", phone: "", email: "" });
      fetchCustomers();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al actualizar cliente"));
    }
  };

  const handleDeleteCustomer = async () => {
    try {
      await axiosInstance.delete(`/customers/${selectedCustomer.id}?password=${password}`);
      toast.success("Cliente eliminado exitosamente");
      setShowDeleteDialog(false);
      setSelectedCustomer(null);
      setPassword("");
      fetchCustomers();
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al eliminar cliente"));
    }
  };

  const openEditDialog = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name,
      cedula: customer.cedula || "",
      phone: customer.phone || "",
      email: customer.email || ""
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (customer) => {
    setSelectedCustomer(customer);
    setShowDeleteDialog(true);
  };

  // Download Excel template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get('/customers/template');
      if (response.data.success) {
        const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.excel_base64}`;
        const downloadLink = document.createElement("a");
        downloadLink.href = linkSource;
        downloadLink.download = response.data.filename;
        downloadLink.click();
        toast.success("Plantilla descargada");
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al descargar plantilla"));
    }
  };

  // Import customers from Excel
  const handleImportExcel = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!selectedBox) {
      toast.error("Selecciona una caja primero");
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('box_id', selectedBox.id);

    try {
      const response = await axiosInstance.post('/customers/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        fetchCustomers();
        setShowImportDialog(false);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al importar clientes"));
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return <div className="text-center py-20">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Clientes
          </h1>
          <p className="text-slate-600">Gestiona la información de tus clientes</p>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            Plantilla Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowImportDialog(true)}
            className="gap-2 border-green-300 text-green-700 hover:bg-green-50"
          >
            <Upload className="w-4 h-4" />
            Importar Excel
          </Button>
          <Button
            data-testid="add-customer-button"
            onClick={() => setShowDialog(true)}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
          >
            <Plus className="w-4 h-4" />
            Nuevo Cliente
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {customers.length === 0 ? (
          <Card className="col-span-full bg-white/80 backdrop-blur-xl border-slate-200">
            <CardContent className="py-12">
              <p className="text-center text-slate-500">No hay clientes registrados</p>
            </CardContent>
          </Card>
        ) : (
          customers.map((customer) => (
            <Card
              key={customer.id}
              data-testid={`customer-${customer.id}`}
              className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg hover:shadow-xl transition-shadow"
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-lg">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    {customer.customer_code && (
                      <span className="inline-block rounded bg-blue-100 text-blue-700 px-2 py-0.5 text-xs font-mono font-bold" title="Código de cliente">#{customer.customer_code}</span>
                    )}
                    {customer.name}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      data-testid={`history-customer-${customer.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => { setHistoryCustomer(customer); setShowHistoryDialog(true); }}
                      className="hover:bg-slate-100 hover:text-slate-700"
                      title="Ver historial completo"
                    >
                      <Clock className="w-4 h-4" />
                    </Button>
                    {user.role === 'master' && (
                      <>
                        <Button
                          data-testid={`edit-customer-${customer.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(customer)}
                          className="hover:bg-blue-50 hover:text-blue-600"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-customer-${customer.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(customer)}
                          className="hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {customer.cedula && (
                  <div className="flex items-center gap-2 text-sm text-slate-600" data-testid={`customer-cedula-${customer.id}`}>
                    <span className="font-semibold text-slate-500">ID:</span>
                    {customer.cedula}
                  </div>
                )}
                {customer.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="w-4 h-4" />
                    {customer.phone}
                  </div>
                )}
                {customer.email && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail className="w-4 h-4" />
                    {customer.email}
                  </div>
                )}
                {!!customer.total_credit_sales && (
                  <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2" data-testid={`customer-credit-badge-${customer.id}`}>
                    <span className="font-semibold">{customer.total_credit_sales}</span>
                    venta{customer.total_credit_sales === 1 ? "" : "s"} a crédito
                    {customer.last_credit_sale_at && (
                      <span className="ml-1 text-xs text-amber-600">· última: {String(customer.last_credit_sale_at).slice(0, 10)}</span>
                    )}
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-2">
                  Registrado: {new Date(customer.created_at).toLocaleDateString('es-CR')}
                </p>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo Cliente</DialogTitle>
            <DialogDescription>Registra un nuevo cliente</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre Completo</Label>
              <Input
                id="name"
                data-testid="customer-name-input"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="mt-1.5"
                placeholder="Juan Pérez"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="cedula">Cédula (opcional)</Label>
              <Input
                id="cedula"
                data-testid="customer-cedula-input"
                type="text"
                value={formData.cedula || ""}
                onChange={(e) => setFormData({...formData, cedula: e.target.value})}
                className="mt-1.5"
                placeholder="1-1234-5678"
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Número de Teléfono (identifica al cliente)</Label>
              <Input
                id="phone"
                data-testid="customer-phone-input"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="mt-1.5"
                placeholder="+506 8888-8888"
              />
              <p className="text-xs text-slate-500 mt-1">El teléfono identifica al cliente. Si lo dejas vacío, se usará el código de cliente como marcador para que el MAESTRO lo actualice luego.</p>
            </div>
            
            <div>
              <Label htmlFor="email">Correo Electrónico (Opcional)</Label>
              <Input
                id="email"
                data-testid="customer-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="mt-1.5"
                placeholder="cliente@ejemplo.com"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-customer-button"
              onClick={handleCreateCustomer}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!formData.name}
            >
              Crear Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent data-testid="edit-customer-dialog">
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
            <DialogDescription>Actualiza la información del cliente</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit_name">Nombre Completo</Label>
              <Input
                id="edit_name"
                data-testid="edit-customer-name-input"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="mt-1.5"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit_cedula">Cédula (opcional)</Label>
              <Input
                id="edit_cedula"
                data-testid="edit-customer-cedula-input"
                type="text"
                value={formData.cedula || ""}
                onChange={(e) => setFormData({...formData, cedula: e.target.value})}
                className="mt-1.5"
                placeholder="1-1234-5678"
              />
            </div>
            
            <div>
              <Label htmlFor="edit_phone">Número de Teléfono (identifica al cliente)</Label>
              <Input
                id="edit_phone"
                data-testid="edit-customer-phone-input"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="mt-1.5"
              />
              <p className="text-xs text-slate-500 mt-1">Si lo dejas vacío, se usará el código de cliente como marcador.</p>
            </div>
            
            <div>
              <Label htmlFor="edit_email">Correo Electrónico</Label>
              <Input
                id="edit_email"
                data-testid="edit-customer-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="edit_password">Contraseña Master</Label>
              <Input
                id="edit_password"
                data-testid="edit-customer-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5"
                placeholder="Ingresa la contraseña master"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditDialog(false);
              setPassword("");
              setFormData({ name: "", cedula: "", phone: "", email: "" });
            }}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-edit-customer-button"
              onClick={handleEditCustomer}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!formData.name || !password}
            >
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-customer-dialog">
          <DialogHeader>
            <DialogTitle>Eliminar Cliente</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de eliminar a {selectedCustomer?.name}? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label htmlFor="delete_password">Contraseña Master</Label>
            <Input
              id="delete_password"
              data-testid="delete-customer-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5"
              placeholder="Ingresa la contraseña master"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteDialog(false);
              setPassword("");
            }}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-delete-customer-button"
              onClick={handleDeleteCustomer}
              variant="destructive"
              disabled={!password}
            >
              Eliminar Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Excel Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Importar Clientes desde Excel
            </DialogTitle>
            <DialogDescription>
              Sube un archivo Excel con la lista de clientes a importar
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">Formato del archivo:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>NOMBRE</strong> - Obligatorio (se convierte a mayúsculas)</li>
                <li>• <strong>TELEFONO</strong> - Opcional</li>
                <li>• <strong>EMAIL</strong> - Opcional</li>
              </ul>
              <p className="text-xs text-green-600 mt-2">
                ✓ Puedes importar clientes que ya existen en otras cajas. Solo se omiten duplicados dentro de la misma caja.
              </p>
            </div>

            <div className="flex flex-col items-center gap-3">
              <Button
                variant="outline"
                onClick={handleDownloadTemplate}
                className="w-full gap-2"
              >
                <Download className="w-4 h-4" />
                Descargar Plantilla de Ejemplo
              </Button>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImportExcel}
                accept=".xlsx,.xls"
                className="hidden"
              />
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="w-full gap-2 bg-green-600 hover:bg-green-700"
              >
                {importing ? (
                  <>Importando...</>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Seleccionar Archivo Excel
                  </>
                )}
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historial completo del cliente */}
      <CustomerHistoryDialog
        open={showHistoryDialog}
        onOpenChange={setShowHistoryDialog}
        customerId={historyCustomer?.id}
        customerName={historyCustomer?.name}
        boxId={selectedBox?.id}
      />
    </div>
  );
}